import {
  checkmarkCircle,
  checkmarkCircleOutline,
  closeCircle,
  closeCircleOutline,
  hourglass,
  hourglassOutline,
  removeCircle,
  removeCircleOutline,
  time,
  timeOutline,
} from "ionicons/icons";

// Domain
export const SUBDOMAIN: string = "dashboard";
export const DOMAIN: string = "odonto.ph";

// Date utilities
export const DATE_PLACEHOLDER: string = "2000-01-01";

// Deep Link
export const DEEPLINK_DENTIST_ID_PARAMETER_KEY: string = "dentistId";

// Authentication & User
export const MOBILE_NUMBER_LENGTH: number = 10;
export const OTP_LENGTH: number = 6;
export const OTP_EXPIRY_IN_SECONDS: number = 180;

export const OTP_LOADING_DELAY_IN_MS: number = 172;
export const OTP_ERRORS: { [key: string]: string } = {
  MOBILE_NUMBER_IS_REQUIRED: "Mobile number is required",
  NO_CLINIC_OR_DENTIST_FOUND:
    "No clinic or dentist found with this mobile number. Please sign up.",
  FAILED_TO_CREATE_CLINIC: "Failed to create clinic. Please try again.",
  FAILED_TO_SEND_OTP: "Failed to send OTP. Please click resend.",
  FAILED_TO_RESEND_OTP: "Failed to resend OTP. Please try again.",
};

export const FETCH_USER_TIMEOUT_IN_MS: number = 10000;
export const SIGN_IN_TIMEOUT_IN_MS: number = 10000;
export const SIGN_OUT_TIMEOUT_IN_MS: number = 10000;
export const REFRESH_TIMEOUT_IN_MS: number = 240;

export const SIGN_IN_STEPS: { [key: string]: string } = {
  MOBILE_NUMBER: "mobileNumber",
  VERIFICATION: "verification",
};

export const GET_STARTED_STEPS: { [key: string]: string } = {
  CLINIC_NAME: "clinicName",
  MOBILE_NUMBER: "mobileNumber",
  VERIFICATION: "verification",
};

// UI Constants
export const WELCOME_PAGE_MINIMUM_HEIGHT_FOR_IMAGE: number = 842;
export const ENTRIES_PER_PAGE: number = 3;

export const AVATAR_COLORS: { [key: string]: string } = {
  BLUE: "blue",
  GREEN: "green",
  ORANGE: "orange",
  YELLOW: "yellow",
  BLACK: "black",
  VIOLET: "violet",
};

// Request & Appointment Status
export const REQUEST_STATUS_OPTIONS: { [key: string]: string } = {
  PENDING: "pending",
  APPROVED: "approved",
  DECLINED: "declined",
  RESCHEDULED: "rescheduled",
  CANCELLED: "cancelled",
};

export const ATTENDANCE_STATUS_OPTIONS: { [key: string]: string } = {
  DEFAULT: "default",
  NO_SHOW: "no-show",
  LATE: "late",
};

export const REQUEST_ACTIONS: { [key: string]: string } = {
  APPROVE: "approve",
  DECLINE: "decline",
  RESCHEDULE: "reschedule",
  CANCEL: "cancel",
  MARK_AS_LATE: "mark_as_late",
  MARK_AS_NO_SHOW: "mark_as_no_show",
};

export const REQUEST_ACTION_ICONS: { [key: string]: string } = {
  approve: checkmarkCircleOutline,
  decline: closeCircleOutline,
  reschedule: timeOutline,
  cancel: removeCircleOutline,
};

export const REQUEST_STATUS_ICONS: { [key: string]: string } = {
  pending: hourglassOutline,
  approved: checkmarkCircleOutline,
  declined: closeCircleOutline,
  rescheduled: timeOutline,
  cancelled: removeCircleOutline,
};

export const REQUEST_STATUS_ACTIVE_ICONS: { [key: string]: string } = {
  pending: hourglass,
  approved: checkmarkCircle,
  declined: closeCircle,
  rescheduled: time,
  cancelled: removeCircle,
};

export const REQUEST_RESCHEDULE_METHODS: { [key: string]: string } = {
  MANUAL: "manual",
  AUTOMATIC: "automatic",
};

export const REQUEST_EXPIRATION_BUFFER_IN_MINUTES: number = 30;
export const FETCH_REQUESTS_TIMEOUT_IN_MS: number = 240;

// User Profile & Settings
export const GENDER_OPTIONS: { [key: string]: string } = {
  MALE: "male",
  FEMALE: "female",
  OTHER: "other",
};

export const GENDER_LABELS: { [key: string]: string } = {
  MALE: "Male",
  FEMALE: "Female",
  OTHER: "Prefer not to say",
};

export const ACCESS_TYPES: { [key: string]: string } = {
  NONE: "none",
  EDIT: "edit",
  VIEW: "view",
};

export const ACCESS_TYPE_LABELS: { [key: string]: string } = {
  NONE: "No access",
  EDIT: "Can manage appointments and requests",
  VIEW: "Can view appointments and requests",
};

// Dentist & Services
export const ADD_DENTIST_STEPS: { [key: string]: string } = {
  NAME: "name",
  HOURS: "hours",
  SERVICES: "services",
};

export const DAY_NAMES_ARRAY: string[] = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const SERVICE_DURATION_OPTIONS: { label: string; value: number }[] = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "1 hour", value: 60 },
  { label: "1 hour 15 minutes", value: 75 },
  { label: "1 hour 30 minutes", value: 90 },
  { label: "1 hour 45 minutes", value: 105 },
  { label: "2 hours", value: 120 },
  { label: "2 hours 15 minutes", value: 135 },
  { label: "2 hours 30 minutes", value: 150 },
  { label: "2 hours 45 minutes", value: 165 },
  { label: "3 hours", value: 180 },
  { label: "3 hours 15 minutes", value: 195 },
  { label: "3 hours 30 minutes", value: 210 },
  { label: "3 hours 45 minutes", value: 225 },
  { label: "4 hours", value: 240 },
  { label: "4 hours 15 minutes", value: 255 },
  { label: "4 hours 30 minutes", value: 270 },
  { label: "4 hours 45 minutes", value: 285 },
  { label: "5 hours", value: 300 },
  { label: "5 hours 15 minutes", value: 315 },
  { label: "5 hours 30 minutes", value: 330 },
  { label: "5 hours 45 minutes", value: 345 },
  { label: "6 hours", value: 360 },
  { label: "6 hours 15 minutes", value: 375 },
  { label: "6 hours 30 minutes", value: 390 },
  { label: "6 hours 45 minutes", value: 405 },
  { label: "7 hours", value: 420 },
  { label: "7 hours 15 minutes", value: 435 },
  { label: "7 hours 30 minutes", value: 450 },
  { label: "7 hours 45 minutes", value: 465 },
  { label: "8 hours", value: 480 },
];

export const SPECIALIZATION_OPTIONS: { [key: string]: string } = {
  ANESTHESIOLOGY: "Dental Anesthesiology",
  COSMETIC: "Cosmetic Dentistry",
  ENDODONTICS: "Endodontics",
  GENERAL: "General Dentistry",
  GERIATRIC: "Geriatric Dentistry",
  IMPLANTOLOGY: "Implantology",
  ORTHODONTICS: "Orthodontics",
  PATHOLOGY: "Oral Pathology",
  PEDIATRICS: "Pediatric Dentistry",
  PERIODONTICS: "Periodontics",
  PROSTHODONTICS: "Prosthodontics",
  RADIOLOGY: "Oral Radiology",
  RESTORATIVE: "Restorative Dentistry",
  SURGERY: "Oral Surgery",
  TMJ: "TMJ Specialist",
};

// Notifications & Reports
export const NOTIFICATIONS_PAGE_SIZE: number = 5;

// PostgREST silently truncates results at this size unless callers paginate.
export const POSTGREST_PAGE_SIZE: number = 1000;

export const NOTIFICATION_CATEGORIES: { [key: string]: string } = {
  REMINDER: "reminder",
  REQUEST: "request",
  CANCELLED: "cancelled",
  RESCHEDULED: "rescheduled",
  WEEKLY_REPORT: "weekly_report",
};

export const NOTIFICATION_CATEGORY_IDENTIFIERS: { [key: string]: string } = {
  REMINDER: "Reminder: ",
  REQUEST: "requested",
  CANCELLED: "cancelled",
  RESCHEDULED: "rescheduled",
  WEEKLY_REPORT: "this week",
};

export const NOTIFICATION_TITLES: { [key: string]: string } = {
  [NOTIFICATION_CATEGORIES.REMINDER]: "You have an upcoming appointment!",
  [NOTIFICATION_CATEGORIES.REQUEST]: "You just received a new request!",
  [NOTIFICATION_CATEGORIES.CANCELLED]: "Your appointment has been cancelled!",
  [NOTIFICATION_CATEGORIES.RESCHEDULED]:
    "Your appointment has been rescheduled!",
  [NOTIFICATION_CATEGORIES.WEEKLY_REPORT]: "Your weekly report is ready!",
};

export const NOTIFICATION_ACTIONS: { [key: string]: string } = {
  [NOTIFICATION_CATEGORIES.WEEKLY_REPORT]: "View Report",
  [NOTIFICATION_CATEGORIES.REQUEST]: "View Request",
  [NOTIFICATION_CATEGORIES.REMINDER]: "View Appointment",
  [NOTIFICATION_CATEGORIES.CANCELLED]: "View Appointment",
  [NOTIFICATION_CATEGORIES.RESCHEDULED]: "View Appointment",
};

export const WEEKLY_REPORT_SUBSCRIPTION_TYPES: { [key: string]: string } = {
  CLINIC: "clinic",
  PERSONAL: "personal",
  NONE: "none",
};

export const NOTIFICATION_ACTION_DELAY_IN_MS: number = 241;

// Calendar & Time
export const CALENDAR_MONTHS_DISPLAY_RANGE_PAST: number = 1;
export const CALENDAR_MONTHS_DISPLAY_RANGE_FUTURE: number = 3;

export const OFF_DUTY_OPTIONS: { [key: string]: string } = {
  WHOLE_DAY: "wholeDay",
  TIME_RANGE: "timeRange",
};

// Misc
export const SERVICE_JOIN_SEPARATOR: string = ", ";
export const PURPOSE_JOIN_SEPARATOR: string = ", ";
export const SPECIALIZATION_JOIN_SEPARATOR: string = ", ";
export const SELECT_MODAL_OTHERS: string = "Others";
export const OFF_DUTY_PATIENT_ID: string =
  "ad12af66-5edb-4d67-a91e-bd73d96d074d";

// Platform & URLs
export const PLATFORMS: { [key: string]: string } = {
  IOS: "ios",
  ANDROID: "android",
  WEB: "web",
};

export const APP_STORE_ID: string = "6737960471";
export const HELP_URL: string = "https://www.odonto.ph/support/help";
export const TERMS_OF_SERVICE_URL: string =
  "https://www.odonto.ph/legal/terms-of-service";
export const PRIVACY_POLICY_URL: string =
  "https://www.odonto.ph/legal/privacy-policy";

// Weekly Report
export const WEEKLY_REPORT_DEFAULT_DENTIST: string = "All Dentists";
export const WEEKLY_REPORT_DEFAULT_CHART_WIDTH: number = 320;
export const WEEKLY_REPORT_DEFAULT_CHART_HEIGHT: number = 240;
export const WEEKLY_REPORT_DEFAULT_CHART_MAX_WIDTH: number = 720;
export const AVERAGE_REVENUE_PER_APPOINTMENT: number = 800;

// Overview Table
export const OVERVIEW_TABLE = {
  SLOT_WIDTH: 240,
  HALF_SLOT_WIDTH: 120,
  PIXELS_PER_MINUTE: 4,
  MIN_APPOINTMENT_WIDTH: 60,
  MIN_TIME_COLUMNS: 3,
} as const;

// Profile Popups
export const POPUP_IDENTIFIERS: { [key: string]: string } = {
  WEEKLY_REPORT: "weeklyReport",
  NOTIFICATIONS: "notifications",
};

// Clinic Name
export const CLINIC_NAME_IDENTIFIERS: string[] = [
  "Dental",
  "Dental Center",
  "Clinic",
  "Teeth",
];

// Sorting
export const SORT_DIRECTIONS: { [key: string]: string } = {
  ASC: "asc",
  DESC: "desc",
};

export const DEFAULT_FIELDS: { [key: string]: string } = {
  ID: "id",
};

// Tables
export const TABLES: { [key: string]: string } = {
  DENTISTS: "dentists",
  PATIENTS: "patients",
  CLINICS: "clinics",
  SERVICES: "services",
  HMO_PROVIDERS: "hmo_providers",
  REQUESTS: "requests",
  USERS: "users",
  SHARES: "shares",
  DENTIST_HOURS: "dentist_hours",
  DENTIST_NOTIFICATIONS: "dentist_notifications",
  WEEKLY_REPORTS: "weekly_reports",
  SERVICE_OPTIONS: "service_options",
  HMO_PROVIDER_OPTIONS: "hmo_provider_options",
  SUPPORTED_VERSIONS: "supported_versions",
};

export const TABLES_WITH_DELETED_AT: string[] = [
  TABLES.DENTISTS,
];

export const PROCEDURE_TERMS_TO_EXCLUDE: string[] = [
  "(Bunot)",
  "(Pustiso)",
  "(Painless)",
  "(Filling/Pasta)",
  "(Front Tooth)",
  "(Cavity Prevention)",
  "(Consultation)",
];

export const OFF_DUTY_PURPOSE: string =
  "This time slot is blocked. Patients will be prompted to select a different date or time.";

// Account Management
export const STORED_ACCOUNTS_KEY: string = "storedAccounts";
export const MOBILE_NUMBERS_KEY: string = "mobileNumbers";
export const SAVE_ACCOUNT_DELAY_IN_MS: number = 124;
